import { m, useReducedMotion } from "framer-motion";

/**
 * On-theme illustration of the read-only care sheet a caregiver sees.
 *
 * Deliberately sober — this artwork signals trust, so it stays in `font-sans`
 * and carries only two tasteful, one-time touches: the green verified
 * checkmark draws itself in, and the "SEVERE" badge gives a single gentle
 * pulse. Both are skipped (rendered at their final state) under reduced motion.
 */
export function CareCard() {
  const prefersReduced = useReducedMotion();

  return (
    <svg
      viewBox="0 0 360 384"
      className="w-full max-w-sm font-sans drop-shadow-2xl"
      role="img"
      aria-label="Example care profile a caregiver sees"
    >
      <rect x="8" y="8" width="344" height="368" rx="24" fill="#ffffff" stroke="#e7e5e4" strokeWidth="1.5" />
      <circle cx="50" cy="58" r="24" fill="#bae6fd" />
      <text x="50" y="66" textAnchor="middle" fontSize="22" fontWeight="700" fill="#0369a1">E</text>
      <rect x="86" y="45" width="132" height="13" rx="6.5" fill="#1c1917" />
      <rect x="86" y="67" width="86" height="9" rx="4.5" fill="#d6d3d1" />
      <g transform="translate(286, 40)">
        <circle cx="18" cy="18" r="18" fill="#ecfdf5" />
        <path d="M18 8 l8 3 v6 c0 5 -3.5 8.5 -8 10 c-4.5 -1.5 -8 -5 -8 -10 v-6 z" fill="#10b981" />
        <m.path
          d="M14.5 18 l2.5 2.5 l4.5 -5"
          stroke="#ffffff"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={prefersReduced ? false : { pathLength: 0 }}
          whileInView={prefersReduced ? undefined : { pathLength: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeInOut", delay: 0.5 }}
        />
      </g>
      <rect x="28" y="98" width="304" height="1.5" fill="#f5f5f4" />
      <text x="28" y="124" fontSize="10" fontWeight="700" fill="#a8a29e" letterSpacing="1">ALLERGIES</text>
      <circle cx="34" cy="146" r="5" fill="#f43f5e" />
      <text x="48" y="151" fontSize="13.5" fontWeight="600" fill="#292524">Peanuts</text>
      <m.g
        initial={prefersReduced ? false : { scale: 1 }}
        whileInView={prefersReduced ? undefined : { scale: [1, 1.08, 1] }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.9, ease: "easeInOut", delay: 0.9, times: [0, 0.5, 1] }}
      >
        <rect x="250" y="137" width="82" height="20" rx="10" fill="#fee2e2" />
        <text x="291" y="151" textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#b91c1c">SEVERE</text>
      </m.g>
      <text x="28" y="186" fontSize="10" fontWeight="700" fill="#a8a29e" letterSpacing="1">MEDICATIONS</text>
      <circle cx="34" cy="208" r="5" fill="#0ea5e9" />
      <text x="48" y="213" fontSize="13.5" fontWeight="600" fill="#292524">Albuterol</text>
      <text x="332" y="213" textAnchor="end" fontSize="12" fill="#78716c">as needed</text>
      <text x="28" y="248" fontSize="10" fontWeight="700" fill="#a8a29e" letterSpacing="1">EMERGENCY CONTACT</text>
      <circle cx="34" cy="270" r="5" fill="#22c55e" />
      <text x="48" y="275" fontSize="13.5" fontWeight="600" fill="#292524">Mom</text>
      <text x="332" y="275" textAnchor="end" fontSize="12" fill="#78716c">0917 555 0140</text>
      <rect x="28" y="306" width="304" height="44" rx="14" fill="#f0f9ff" />
      <g transform="translate(44, 320)">
        <rect x="0" y="6" width="16" height="12" rx="2.5" fill="#0284c7" />
        <path d="M3 6 v-2 a5 5 0 0 1 10 0 v2" stroke="#0284c7" strokeWidth="2" fill="none" />
      </g>
      <text x="70" y="333" fontSize="12.5" fontWeight="600" fill="#0c4a6e">Shared securely · expires in 24h</text>
    </svg>
  );
}
